import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { usePropertyDefinitions } from '../../hooks/useQueries'
import { EnumPropertyDisplay } from '../properties/PropertyInputUtils'

export interface PersonUser {
  uid: string
  name?: string
  // Present on every row from the parent's users-list fetch; not read by
  // this component (it only ever receives already-filtered people) but kept
  // here so the route can filter `children` into this narrower type without
  // a separate cast.
  type?: string
  properties?: Record<string, unknown>
}

interface PeopleSectionProps {
  org: string
  people: PersonUser[]
}

// Lists a company's child `contact` users with their `contact_role` (a
// PropertyDefinition enum — see res-provisioning/types/01-properties.json).
// Adding a person reuses the page's existing "Create Child Contact"
// button/flow unconditionally rendered in the header; this section is purely
// display, hidden entirely when the company has none (mirrors the existing
// "Child Contacts" block further down this same page).
export function PeopleSection({ org, people }: PeopleSectionProps) {
  const { t } = useTranslation('users')
  const { data: userPropertyDefs = [] } = usePropertyDefinitions(org, { entityType: 'user' })
  const contactRoleDef = userPropertyDefs.find((pd) => pd.code === 'contact_role')

  if (people.length === 0) return null

  return (
    <div className="bg-card rounded-lg border border-border p-6" data-testid="people-section">
      <h2 className="text-lg font-semibold text-foreground mb-4">{t('people.title', { count: people.length })}</h2>
      <div className="divide-y divide-border">
        {people.map((person) => {
          const role = person.properties?.contact_role as string | string[] | undefined
          return (
            <div key={person.uid} className="flex items-center justify-between py-3" data-testid={`person-row-${person.uid}`}>
              <Link
                to="/$org/users/$userUid"
                params={{ org, userUid: person.uid }}
                className="text-primary hover:text-primary/80 hover:underline text-sm"
                data-testid={`person-link-${person.uid}`}
              >
                {person.name || '-'}
              </Link>
              <span className="text-sm text-muted-foreground" data-testid={`person-role-${person.uid}`}>
                {role && contactRoleDef?.uid ? (
                  <EnumPropertyDisplay propertyDefinitionUid={contactRoleDef.uid} value={role} org={org} />
                ) : (
                  <span className="italic">{t('people.no_role')}</span>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
